import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";

const BURG = { background: "#8B1E1E" };
const NEW = "__new__";

const empty = { name: "", product_type: "raw", cost: "", category_id: "", new_category: "", unit_id: "", new_unit_name: "", new_unit_code: "" };

export default function NewItemDialog({ open, onOpenChange, onCreated }) {
  const [cats, setCats] = useState([]);
  const [units, setUnits] = useState([]);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (open) {
      setF(empty);
      api.get("/categories").then(setCats).catch(() => {});
      api.get("/units").then(setUnits).catch(() => {});
    }
  }, [open]);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Item name is required"); return; }
    const payload = { name: f.name.trim(), product_type: f.product_type, cost: Number(f.cost || 0) };
    if (f.category_id === NEW) payload.new_category = f.new_category;
    else if (f.category_id) payload.category_id = f.category_id;
    if (f.unit_id === NEW) payload.new_unit = { name: f.new_unit_name, code: f.new_unit_code || f.new_unit_name };
    else if (f.unit_id) payload.unit_id = f.unit_id;
    setSaving(true);
    try {
      const res = await api.post("/inventory/items", payload);
      toast.success(`Item "${res.product.name}" created`);
      onCreated && onCreated(res.product);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-brand">New Inventory Item</DialogTitle>
          <DialogDescription>Add a raw material or stock item to the master list. It becomes available in every stock action.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5"><Label>Item name</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Mozzarella Cheese" data-testid="newitem-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={f.product_type} onValueChange={(v) => set("product_type", v)}>
                <SelectTrigger data-testid="newitem-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="raw">Raw material</SelectItem><SelectItem value="stock">Stock item</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Unit cost (₦)</Label>
              <Input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="newitem-cost" /></div>
          </div>
          <div className="space-y-1.5"><Label>Category</Label>
            <Select value={f.category_id} onValueChange={(v) => set("category_id", v)}>
              <SelectTrigger data-testid="newitem-category"><SelectValue placeholder="Select category…" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                <SelectItem value={NEW}>＋ Add new category…</SelectItem></SelectContent></Select>
            {f.category_id === NEW && <Input className="mt-2" value={f.new_category} onChange={(e) => set("new_category", e.target.value)} placeholder="New category name" data-testid="newitem-new-category" />}</div>
          <div className="space-y-1.5"><Label>Base unit (recipe UOM)</Label>
            <Select value={f.unit_id} onValueChange={(v) => set("unit_id", v)}>
              <SelectTrigger data-testid="newitem-unit"><SelectValue placeholder="Select unit…" /></SelectTrigger>
              <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.code ? ` (${u.code})` : ""}</SelectItem>)}
                <SelectItem value={NEW}>＋ Add new unit…</SelectItem></SelectContent></Select>
            {f.unit_id === NEW && <div className="grid grid-cols-2 gap-2 mt-2">
              <Input value={f.new_unit_name} onChange={(e) => set("new_unit_name", e.target.value)} placeholder="Name e.g. Kilogram" data-testid="newitem-new-unit-name" />
              <Input value={f.new_unit_code} onChange={(e) => set("new_unit_code", e.target.value)} placeholder="Code e.g. kg" data-testid="newitem-new-unit-code" /></div>}</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="newitem-save" style={BURG} className="text-white">{saving ? "Creating…" : "Create Item"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
